import React, { useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import moment from 'moment';

import { SkeletonCards } from '../SpotlightsSkeleton';
import { getModelEval } from '../../../api/spotlights';

const pct = v => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);
const fixed = (v, digits = 4) => (v == null ? '—' : Number(v).toFixed(digits));
const int = v => (v == null ? '—' : Number(v).toLocaleString());

// Renders the recommendation shadow-eval digest (GET /api/AdminSpotlights/model-eval): the
// accuracy + calibration evidence the model-verdict rollout gates on. Internal only.
const SpotlightsModel = () => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async refresh => {
    setLoading(true);
    setError(null);
    try {
      setPayload(await getModelEval(refresh ? { refresh: 1 } : undefined));
    } catch (err) {
      setError(typeof err === 'string' ? err : 'Failed to load the model eval');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const report = payload?.report;
  const dq = report?.decisionQuality;

  return (
    <div className='spotlights-model-tab'>
      <div className='sl-section' style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div className='sl-section-title'>Recommendation model — shadow eval</div>
          {payload && (
            <div style={{ fontSize: 12, opacity: 0.65 }}>
              Computed {moment(payload.computedAt).fromNow()}
              {payload.cached ? ' · cached' : ''} · graded against realized day-30 Ad Network ROAS
            </div>
          )}
        </div>
        <button className='sl-pill' disabled={loading} onClick={() => load(true)}>
          {loading ? 'Computing…' : 'Recompute'}
        </button>
      </div>

      {error && <div className='sl-error'>{error}</div>}
      {!payload ? (
        error ? null : <SkeletonCards count={6} />
      ) : !report ? (
        <div className='sl-section'>No SpotlightAnalysis predictions yet — the producer has not pushed any scored runs.</div>
      ) : (
        <div className={cn('sl-refreshable', { 'sl-refreshing': loading })}>
          <div className='sl-card-grid'>
            <Card label='Graded predictions' value={int(report.totals.graded)} sub={`${int(report.totals.predictions)} total · ${int(report.totals.pendingMaturity)} pending maturity`} />
            <Card
              label='Coverage'
              value={pct(report.coverage.share)}
              sub={`${int(report.coverage.withFreshRun)} of ${int(report.coverage.liveGradeableSpotlights)} live with a run < ${report.coverage.freshWithinHours}h old`}
              alert={report.coverage.share != null && report.coverage.share < 0.6}
            />
            <Card label='Log loss' value={fixed(report.calibration.logLoss)} sub={`Brier ${fixed(report.calibration.brier)} · n=${int(report.calibration.n)}`} />
            <Card
              label='Pause precision'
              value={pct(dq.modelPause.correctlyBelowTarget)}
              sub={`${pct(dq.modelPause.wouldHaveBeatenTarget)} would have beaten target · n=${int(dq.modelPause.n)} · $${int(Math.round(dq.modelPause.spendAfterPrediction || 0))} spend after prediction`}
              alert={dq.modelPause.correctlyBelowTarget != null && dq.modelPause.correctlyBelowTarget < 0.9}
            />
            <Card label='Keep-running failure rate' value={pct(dq.modelKeepRunning.realizedFailureRate)} sub={`n=${int(dq.modelKeepRunning.n)}`} />
            <Card
              label='Model age'
              value={report.drift.modelTrainedAgeDays != null ? `${report.drift.modelTrainedAgeDays}d` : 'unknown'}
              sub={`latest run ${report.drift.latestRunAt ? moment(report.drift.latestRunAt).fromNow() : '—'}`}
              alert={report.drift.stale}
            />
          </div>

          <div className='sl-section'>
            <div className='sl-section-title'>Calibration by predicted-success decile</div>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th>Bucket</th>
                  <th>n</th>
                  <th>Mean predicted</th>
                  <th>Realized success</th>
                </tr>
              </thead>
              <tbody>
                {report.calibration.deciles
                  .filter(d => d.n > 0)
                  .map(d => (
                    <tr key={d.bucket}>
                      <td>{d.bucket}</td>
                      <td>{int(d.n)}</td>
                      <td>{pct(d.meanPredicted)}</td>
                      <td>{pct(d.realizedSuccessRate)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          <div className='sl-section'>
            <div className='sl-section-title'>Model vs reconstructed rules verdict</div>
            <div className='sl-card-grid'>
              <Card label='Both pause' value={int(report.rulesVsModel.bothPause)} />
              <Card label='Both keep running' value={int(report.rulesVsModel.bothKeep)} />
              <Card label='Model-only pause' value={int(report.rulesVsModel.modelOnlyPause)} />
              <Card label='Rules-only pause' value={int(report.rulesVsModel.rulesOnlyPause)} sub={`rules precision ${pct(dq.rulesPause.correctlyBelowTarget)}`} />
            </div>
          </div>

          <div className='sl-section'>
            <div className='sl-section-title'>Accuracy by prediction month</div>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Graded</th>
                  <th>Log loss</th>
                  <th>Brier</th>
                  <th>Pause precision</th>
                </tr>
              </thead>
              <tbody>
                {report.monthly.map(m => (
                  <tr key={m.month}>
                    <td>{m.month}</td>
                    <td>{int(m.n)}</td>
                    <td>{fixed(m.logLoss)}</td>
                    <td>{fixed(m.brier)}</td>
                    <td>
                      {pct(m.pausePrecision)} (n={int(m.pauseN)})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className='sl-section'>
            <div className='sl-section-title'>Target provenance</div>
            <table className='sl-table'>
              <thead>
                <tr>
                  <th>roasSource (producer-reported)</th>
                  <th>Graded</th>
                  <th>Target mismatches</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report.provenance.byRoasSource).map(([source, stats]) => (
                  <tr key={source}>
                    <td>{source}</td>
                    <td>{int(stats.n)}</td>
                    <td>{int(stats.mismatches)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 8 }}>
              Targets came from: {Object.entries(report.provenance.targetSources).map(([source, n]) => `${source} ×${int(n)}`).join(' · ')}. Rules
              verdicts are reconstructed — the engine&apos;s freshness guard can&apos;t be replayed, and setting changes before the at-time stamps
              began fall back to current settings.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Card = ({ label, value, sub, alert }) => (
  <div className={cn('sl-card', { alert })}>
    <div className='sl-card-label'>{label}</div>
    <div className='sl-card-value'>{value}</div>
    {sub && <div className='sl-card-sub'>{sub}</div>}
  </div>
);

export default SpotlightsModel;
